import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";

/**
 * SneakPeekVault — IQ-puzzle-gated reveal of Sean's upcoming projects.
 * Discoverable via the footer "🔓 The Vault" pill OR /?vault=1 deep-link.
 *
 *  1) Fetch puzzles (questions + hints) from /api/vault/puzzles
 *  2) User answers all three
 *  3) Submit to /api/vault/attempt → if all_correct, server issues a token
 *  4) Use token to fetch /api/vault/projects → render the roadmap
 */
const PASS_KEY = "seanlab_vault_pass";

export default function SneakPeekVault({ open, onClose, handle }) {
  const [puzzles, setPuzzles] = useState([]);
  const [answers, setAnswers] = useState(["", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);   // {all_correct, score, total, results, vault_pass}
  const [unlocked, setUnlocked] = useState(false);
  const [projects, setProjects] = useState(null);
  const [err, setErr] = useState("");

  // On open, check if user already holds a pass + load puzzles
  useEffect(() => {
    if (!open) return;
    setErr("");
    fetch(`${API}/vault/puzzles`).then((r) => r.json()).then(setPuzzles).catch(() => setErr("Couldn't load the vault."));
    const cached = localStorage.getItem(PASS_KEY);
    if (cached) {
      // Try to unlock with cached pass
      tryUnlock(cached);
    }
  }, [open]);

  const tryUnlock = async (token) => {
    try {
      const r = await fetch(`${API}/vault/projects`, { headers: { "X-Vault-Pass": token } });
      if (r.ok) {
        const data = await r.json();
        setProjects(data.projects || []);
        setUnlocked(true);
      } else {
        localStorage.removeItem(PASS_KEY);
      }
    } catch {/* network noise */}
  };

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (answers.some((a) => !a.trim())) {
      setErr("Answer all three to attempt the vault.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/vault/attempt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, handle: handle || "" }),
      });
      const data = await r.json();
      setResults(data);
      if (data.all_correct && data.vault_pass) {
        localStorage.setItem(PASS_KEY, data.vault_pass);
        await tryUnlock(data.vault_pass);
      }
    } catch {
      setErr("Couldn't submit your attempt — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers(["", "", ""]);
    setResults(null);
    setUnlocked(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[55] flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      style={{ background: "rgba(14,18,48,0.7)", backdropFilter: "blur(10px)" }}
      onClick={onClose}
      data-testid="vault-modal"
    >
      <div
        className="card max-w-lg w-full animate-pop-in relative my-6"
        onClick={(e) => e.stopPropagation()}
        style={{ padding: "1.4rem 1.3rem 1.2rem", background: "#fff" }}
      >
        <button
          onClick={onClose}
          aria-label="Close vault"
          data-testid="vault-close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-2 border-[var(--ink)] flex items-center justify-center text-xl font-bold leading-none"
          style={{ boxShadow: "2px 2px 0 0 var(--ink)", color: "var(--ink)" }}
        >
          ×
        </button>

        {!unlocked ? (
          <>
            <div className="text-center mb-4">
              <div className="text-5xl mb-2" aria-hidden>🔐</div>
              <div className="eyebrow text-[var(--orange)]">restricted · sciencer iq filter</div>
              <h2 className="font-display text-2xl sm:text-3xl text-[var(--ink)] leading-tight mt-1">
                The Vault
              </h2>
              <p className="text-sm text-[var(--ink-soft)] mt-1">
                Solve all three. Smart enthusiasts only — Sean's upcoming books, toys & inventions wait inside.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="vault-form">
              {puzzles.map((p, i) => {
                const wrong = results && results.results?.[i]?.correct === false;
                const right = results && results.results?.[i]?.correct === true;
                return (
                  <div
                    key={p.id}
                    className="card-flat"
                    style={{
                      background: right ? "#e3f5e2" : wrong ? "#ffe1de" : "var(--paper-2)",
                      borderColor: right ? "#3b9a4a" : wrong ? "var(--red)" : "var(--ink)",
                    }}
                    data-testid={`vault-puzzle-${p.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="pill" style={{ background: "var(--ink)", color: "#fff", fontSize: "0.62rem" }}>
                        #{p.id} · {p.level}
                      </span>
                      {right && <span className="text-xs font-bold text-[#3b9a4a]">✓ correct</span>}
                      {wrong && <span className="text-xs font-bold text-[var(--red)]">✗ try again</span>}
                    </div>
                    <p className="text-sm text-[var(--ink)] leading-snug">{p.question}</p>
                    <input
                      value={answers[i]}
                      onChange={(e) => {
                        const next = [...answers];
                        next[i] = e.target.value;
                        setAnswers(next);
                      }}
                      placeholder="Your answer…"
                      className="field mt-2"
                      data-testid={`vault-input-${p.id}`}
                      disabled={submitting}
                    />
                    <div className="text-[0.68rem] font-mono mt-1.5" style={{ color: "var(--ink-soft)" }}>
                      💡 {p.hint}
                    </div>
                  </div>
                );
              })}

              {err && <div className="text-sm font-bold text-[var(--red)]" data-testid="vault-error">{err}</div>}

              {results && !results.all_correct && (
                <div
                  className="card-flat tint-red text-sm"
                  data-testid="vault-fail"
                >
                  Score: <strong>{results.score} / {results.total}</strong>. The vault stays sealed. Refine your answers and try again.
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn w-full"
                style={{ background: "var(--orange)", color: "#fff" }}
                data-testid="vault-submit"
              >
                {submitting ? "Checking…" : "🔓 Attempt the Vault"}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="text-center mb-4">
              <div className="text-5xl mb-2" aria-hidden>🎉</div>
              <div className="eyebrow text-[var(--orange)]">vault unlocked · sciencer tier</div>
              <h2 className="font-display text-2xl sm:text-3xl text-[var(--ink)] leading-tight mt-1">
                You're In.
              </h2>
              <p className="text-sm text-[var(--ink-soft)] mt-1">
                Sean's upcoming projects — confidential. Don't leak. 🤫
              </p>
            </div>

            <div className="space-y-3" data-testid="vault-projects">
              {(projects || []).map((proj) => (
                <div
                  key={proj.id}
                  className="card-flat tint-orange"
                  data-testid={`vault-project-${proj.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="pill" style={{ background: "var(--ink)", color: "#fff", fontSize: "0.62rem" }}>
                      {proj.category} · {proj.status}
                    </span>
                    <span className="text-[0.7rem] font-mono" style={{ color: "var(--ink-soft)" }}>{proj.eta}</span>
                  </div>
                  <div className="font-display text-lg text-[var(--ink)] leading-tight">{proj.title}</div>
                  <p className="text-sm text-[var(--ink-soft)] mt-1.5 leading-snug">{proj.blurb}</p>
                  {proj.url && (
                    <a
                      href={proj.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid={`vault-project-cta-${proj.id}`}
                      className="inline-block mt-2.5 pill"
                      style={{ background: "var(--orange)", color: "#fff", padding: "0.35rem 0.8rem" }}
                    >
                      {proj.cta || "Open ↗"}
                    </a>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={reset}
              className="btn-ghost w-full mt-4 text-sm"
              data-testid="vault-reset"
              title="Clear local vault pass (testing)"
            >
              Lock the vault again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
